import { RSAA } from 'redux-api-middleware'
import * as R from 'ramda'
import { getApiPath, makeQueryParams } from 'sidecar-utility-library'
import { types, initialState } from './types'
import { parseCareDetail } from './utils/parseCareDetail'
import { parsePharmacyList } from './utils/parsePharmacyList'
import { getNextRequestId } from './utils/getNextRequestId'
import { isStaleResponse } from './utils/isStaleResponse'

/**
 * Key identifying an episode request, used in place of a per-call nonce.
 *
 * A nonce can't work for the episode call because it is cached: on a cache hit
 * `readCache` answers from `cachedResult` and never lets the RSAA reach
 * `apiMiddleware`, so no REQUEST fires to stamp the nonce into state. Keying on
 * request context instead means the key can be claimed up front — before the
 * cache is consulted — and still match the cached SUCCESS.
 *
 * The endpoint is the context. `readCache` keys `cachedResult` and
 * `pendingRequests` on the endpoint string too, so two requests share this key
 * exactly when the cache considers them the same request: identical params
 * de-duplicate into one response and one key, while a changed filter, location
 * or member produces a different endpoint and therefore a different key. Keying
 * on `uuid` alone would collapse all of those into one, and the episode effect
 * refires on five other params that change the endpoint while the uuid stays
 * put (see `CareEpisode.jsx`), so a superseded response would pass the guard.
 *
 * @param {string} endpoint - The fully built endpoint for this request.
 * @returns {string} Key for the episode request.
 */
const buildEpisodeContextKey = endpoint => `careEpisode:${endpoint}`

export const fetchSimpleCareDetail = ({
  // The param list is really long, however, we can't use the spread
  // operator because we have params that from the /doctors endpoint
  // that get added and should not be passed to the /details endpoint.
  uuid,
  zipCode,
  memberUuid,
  careName,
  drugName,
  category,
  gsn,
  qty,
  location,
  brandedOrGenericId,
  numOfPharmacies,
  locationLat,
  locationLong,
  searchRadius,
  form,
  strength,
  careEpisode, //boolean
  prescriptionInitialLoad, //boolean
  sessionId,
  searchedQuery,
  intentCallId,
}) => {
  const endpoint = getApiPath(
    'care',
    `cares/detail${makeQueryParams({
      uuid,
      zipCode,
      memberUuid,
      careName,
      drugName,
      category,
      gsn,
      qty,
      location,
      brandedOrGenericId,
      numOfPharmacies,
      locationLat,
      locationLong,
      searchRadius,
      form,
      strength,
      careEpisode,
      prescriptionInitialLoad,
      sessionId,
      searchedQuery,
      intentCallId,
    })}`,
  )

  // Tag every phase of this request with the same id so the reducer can tell
  // whether an arriving response is still the one the page is waiting for.
  const meta = { requestId: getNextRequestId('careDetail') }

  return {
    [RSAA]: {
      endpoint,
      method: 'GET',
      types: [
        { type: types.FETCH_CARE_DETAIL_REQUEST, meta },
        { type: types.FETCH_CARE_DETAIL_SUCCESS, meta },
        { type: types.FETCH_CARE_DETAIL_ERROR, meta },
      ],
    },
  }
}

/**
 * Fetch an episode of care.
 *
 * A thunk rather than a plain RSAA so the request can claim `detail.requestId`
 * before the action reaches `readCache`. That ordering is the whole point: the
 * call is cached, and a cache hit is answered from inside `readCache` without a
 * REQUEST ever firing, so claiming the key from the REQUEST phase alone would
 * leave every cache hit looking like a response nobody asked for.
 *
 * Claiming it here closes the other direction too. An episode response used to
 * be applied unconditionally, so an episode request still in flight when the
 * user clicked through to one of its items would land after the detail request
 * and overwrite the item they'd just opened with the bundle they came from
 * (BASH-219).
 */
export const fetchSimpleCareEpisode =
  ({
    uuid,
    zipCode,
    memberUuid,
    category,
    ...searchParams // this is because there are TONS of options
  }) =>
  dispatch => {
    // Built once and used for both the key and the request, so the two cannot
    // drift apart.
    const endpoint = getApiPath(
      'care',
      `cares/detail${makeQueryParams({
        uuid,
        zipCode,
        memberUuid,
        category,
        careEpisode: true,
        ...searchParams,
      })}`,
    )

    const meta = { requestId: buildEpisodeContextKey(endpoint) }

    // MUST come before the RSAA is dispatched, or a cache hit resolves against
    // whatever key the previous page left behind.
    dispatch({ type: types.AWAIT_CARE_DETAIL, meta })

    return dispatch({
      [RSAA]: {
        endpoint,
        method: 'GET',
        types: [
          { type: types.FETCH_CARE_EPISODE_REQUEST, meta },
          { type: types.FETCH_CARE_EPISODE_SUCCESS, meta },
          { type: types.FETCH_CARE_EPISODE_ERROR, meta },
        ],
        cache: true,
      },
    })
  }

export const clearCareDetail = () => ({
  type: types.CLEAR_CARE_DETAIL,
})

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.AWAIT_CARE_DETAIL:
    case types.FETCH_CARE_EPISODE_REQUEST:
    case types.FETCH_CARE_DETAIL_REQUEST: {
      return {
        ...state,
        detail: {
          ...state.detail,
          busy: true,
          error: '',
          requestId: meta.requestId ?? null,
        },
      }
    }
    case types.FETCH_CARE_EPISODE_SUCCESS:
    case types.FETCH_CARE_DETAIL_SUCCESS: {
      // A detail request for a care item the user has already navigated away
      // from must not overwrite the one they're looking at now (BASH-219).
      if (isStaleResponse(meta.requestId, state.detail.requestId)) return state

      // There is middleware (`preParseCareDetail`) that parses out the payload
      // once for all the reducers. We only need to store that result here.
      return {
        ...state,
        detail: {
          ...state.detail,
          error: '',
          entity: payload,
          busy: false,
        },
      }
    }
    case types.FETCH_CARE_EPISODE_ERROR:
    case types.FETCH_CARE_DETAIL_ERROR: {
      // A detail request for a care item the user has already navigated away
      // from must not overwrite the one they're looking at now (BASH-219).
      if (isStaleResponse(meta.requestId, state.detail.requestId)) return state

      return {
        ...state,
        detail: {
          ...state.detail,
          busy: false,
          error: R.pathOr('Error', ['response', 'message'], payload),
        },
      }
    }
    case types.FETCH_RX_PRICING_REQUEST: {
      return {
        ...state,
        detail: {
          ...state.detail,
          pricingBusy: true,
          error: '',
        },
      }
    }
    case types.FETCH_RX_PRICING_ERROR: {
      return {
        ...state,
        detail: {
          ...state.detail,
          pricingBusy: false,
          error: payload.response.message,
        },
      }
    }

    case types.FETCH_RX_PRICING_SUCCESS: {
      const newCareDetails = parseCareDetail(payload)
      return {
        ...state,
        detail: {
          ...state.detail,
          busy: false,
          pricingBusy: false,
          entity: {
            ...state.detail.entity,
            ...newCareDetails,
            pharmacies: parsePharmacyList(payload.pharmacies),
          },
        },
      }
    }

    case types.FETCH_DOCTOR_RATES_SUCCESS: {
      // The rates the detail page is waiting on live in `careRates`, which
      // reduces over this same shared state. A rates response that slice has
      // already discarded as superseded must not take the detail spinner down
      // with it, or the page settles on an empty provider table.
      if (isStaleResponse(meta.rateContext, state.careRates.requestKey)) {
        return state
      }

      return {
        ...state,
        detail: {
          ...state.detail,
          busy: false,
        },
      }
    }

    case types.CLEAR_CARE_DETAIL: {
      return {
        ...state,
        detail: initialState.detail,
      }
    }

    default:
      return state
  }
}
